import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import DialogElement from '../../dialog-element';

gsap.registerPlugin(CustomEase);

const reduceDrawerAnimation = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Drawer = class extends DialogElement {
    constructor() {
        super();
        const clonedTemplate = document.getElementById(this.template).content.cloneNode(true);
        this.attachShadow({ mode: 'open' }).appendChild(clonedTemplate);
        this.shadowRoot.addEventListener('slotchange', (event) => this.updateSlotVisibility(event.target));
    }

    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('aria-modal', 'true');
        this.shadowRoot.querySelector('[part="overlay"]')?.addEventListener('click', this.hide.bind(this), { signal: this.abortController.signal });
        this.shadowRoot.querySelector('[part="outside-close-button"]')?.addEventListener('click', this.hide.bind(this), { signal: this.abortController.signal });
        Array.from(this.shadowRoot.querySelectorAll('slot')).forEach((slot) => this.updateSlotVisibility(slot));
    }

    get template() {
        return this.getAttribute('template') || 'drawer-default-template';
    }

    get shouldLock() { // eslint-disable-line class-methods-use-this
        return true;
    }

    get shouldAppendToBody() { // eslint-disable-line class-methods-use-this
        return true;
    }

    get openFrom() {
        return window.matchMedia('screen and (max-width: 639px)').matches ? 'bottom' : this.getAttribute('open-from') || 'right';
    }

    getClipPathProperties() {
        switch (this.openFrom) {
            case 'left':
                return ['inset(0% 100% 0% 0% round 0.75rem)', 'inset(0% 0% 0% 0.01% round 0.75rem)'];
            case 'right':
                return ['inset(0.01% 0% 0% 100% round 0.75rem)', 'inset(0% 0% 0% 0.01% round 0.75rem)'];
            case 'bottom':
                return ['inset(100% 0% 0% 0.01% round 0.75rem)', 'inset(0.01% 0% 0% 0.01% round 0.75rem)'];
            case 'top':
                return ['inset(0.01% 0.01% 100% 0% round 0.75rem)', 'inset(0.01% 0% 0% 0.01% round 0.75rem)'];

            // no default
        }

        return [];
    }

    setInitialPosition() {
        this.style.left = this.openFrom === 'left' ? '0px' : 'auto';
        this.style.right = this.style.left === 'auto' ? '0px' : 'auto';
        this.style.bottom = this.openFrom === 'bottom' ? '0px' : null;
        this.style.top = this.style.bottom === '' ? '0px' : null;
    }

    showTransition() {
        if (this.shouldLock) {
            // window.lenis.stop();
        }

        let animationControls;

        this.setInitialPosition();

        if (reduceDrawerAnimation) {
            animationControls = gsap.to(this, { autoAlpha: 1, duration: 0.2 });
        } else {
            const content = this.shadowRoot.querySelector('[part="content"]');
            const closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');

            gsap.set(closeButton, { opacity: 0 });

            animationControls = gsap.timeline()
                .to(this, { duration: 0.15, autoAlpha: 1 })
                .fromTo(
                    content,
                    {
                        clipPath: this.getClipPathProperties()[0],
                    },
                    {
                        duration: 0.4,
                        ease: CustomEase.create('custom', 'M0,0 C0.86,0 0.07,1 1,1'),
                        clipPath: this.getClipPathProperties()[1],
                    },
                )
                .to(content.children, { duration: 0.15, opacity: 1 })
                .to(closeButton, { duration: 0.15, opacity: 1 }, '<');
        }

        return animationControls.then(() => this.classList.add('show-close-cursor'));
    }

    hideTransition() {
        if (this.shouldLock) {
            // window.lenis.start();
        }

        let animationControls;

        if (reduceDrawerAnimation) {
            animationControls = gsap.to(this, { autoAlpha: 0, duration: 0.2 });
        } else {
            const content = this.shadowRoot.querySelector('[part="content"]');
            const closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');

            animationControls = gsap.timeline()
                .to(closeButton, { duration: 0.15, opacity: 0 })
                .to(content.children, { duration: 0.15, opacity: 0 }, '<')
                .fromTo(
                    content,
                    {
                        clipPath: this.getClipPathProperties()[1],
                    },
                    {
                        duration: 0.4,
                        ease: CustomEase.create('custom', 'M0,0 C0.86,0 0.07,1 1,1'),
                        clipPath: this.getClipPathProperties()[0],
                    },
                )
                .to(this, { duration: 0.15, autoAlpha: 0 });
        }

        return animationControls.then(() => this.classList.remove('show-close-cursor'));
    }

    updateSlotVisibility(slot) { // eslint-disable-line class-methods-use-this
        if (!['header', 'footer', 'body'].includes(slot.name)) return;
        slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
    }
};

export default Drawer;
